import { Card, Grid, Typography } from '@mui/material';
import { CourseData, LessonData, PublicationStatusEnum } from '../../../../models/CourseData';
import { Svgs } from '../../../../assets/svg';
import { Apps as UnitsIcon } from '@mui/icons-material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { SemiBold } from '../../../../components/SemiBold';
import CourseMenu from './CourseMenu';
import { StatusDotRed } from '../../../../components/StatusDotRed';
import { Spacer } from '../../../../components';
import { filterLessons } from '../../../../data/learning/commands/utils';

export interface CourseItemProps {
  course: CourseData;
}

const METRIC_COLUMN_WIDTH = '70px';
const STATUS_COLUMN_WIDTH = '230px';

const CourseItem: React.FC<CourseItemProps> = ({ course }) => {
  const navigate = useNavigate();
  const isPublished = course.status === PublicationStatusEnum.PUBLISHED;

  const filteredUnits = useMemo(
    () => course.units?.filter(unit => unit.active || unit.status === PublicationStatusEnum.DRAFT),
    [course.units],
  );

  const uniqueLessons = useMemo(
    () =>
      filteredUnits?.reduce((lessons, unit) => {
        const filtered = filterLessons(unit.lessons ?? []);
        return [...lessons, ...filtered];
      }, [] as LessonData[]),
    [filteredUnits],
  );

  const hasUnpublishedUnits = useMemo(
    () => course.units?.some(unit => unit.status === PublicationStatusEnum.DRAFT),
    [course.units],
  );

  const hasUnpublishedLessons = useMemo(
    () =>
      course.units?.some(unit =>
        unit.lessons?.some(lesson => lesson.status === PublicationStatusEnum.DRAFT),
      ),
    [course.units],
  );

  return (
    <Card
      className='course-item-card'
      variant='ghost'
      color='secondary'
      onClick={() => navigate(`${course.originalCourseId}`)}>
      <Grid
        container
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${METRIC_COLUMN_WIDTH} ${METRIC_COLUMN_WIDTH} ${STATUS_COLUMN_WIDTH}`,
          alignItems: 'center',
          columnGap: '20px',
        }}>
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <Svgs.IconBook style={{ flexShrink: 0 }} />
          <Typography
            className='course-name'
            variant='p18Bold'
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}>
            {course.name}
          </Typography>
          {isPublished ? (
            <Svgs.IconCheckmarkGrey style={{ flexShrink: 0, marginRight: '10px' }} />
          ) : (
            <Spacer width='xxl' />
          )}
        </Grid>

        {[
          {
            label: 'Units',
            icon: UnitsIcon,
            count: filteredUnits?.length ?? 0,
            hasUnpublished: hasUnpublishedUnits,
          },
          {
            label: 'Lessons',
            icon: Svgs.IconLightbulb,
            count: uniqueLessons?.length ?? 0,
            hasUnpublished: hasUnpublishedLessons,
          },
        ].map(({ label, icon: Icon, count, hasUnpublished }) => (
          <Grid
            key={label}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
            }}>
            <Typography variant='p12' color='secondary'>
              {label}
            </Typography>
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon fontSize='large' style={{ flexShrink: 0 }} />
              <Typography variant='p20SemiBold' color='secondary'>
                {count}
              </Typography>
              <StatusDotRed visible={hasUnpublished} />
            </Grid>
          </Grid>
        ))}

        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            minWidth: 0,
          }}>
          <Typography
            variant='p16'
            color='secondary'
            sx={{
              textAlign: 'right',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}>
            {isPublished ? (
              <>
                <SemiBold>Published</SemiBold> on{' '}
                {dayjs(course?.publishedTimestamp).format('MM/DD/YY')}
              </>
            ) : (
              'Unpublished'
            )}
          </Typography>
          <Grid sx={{ display: 'flex', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <CourseMenu course={course} />
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export default CourseItem;
